import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, Field, Input, Select } from '../../../components/ui';
import type { UpdateWatchInput, WatchFrequency, WatchView } from '../../../types/monitoring';
import { FREQUENCIES, FREQUENCY_LABEL } from '../format';

const toList = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

export interface WatchEditorDialogProps {
  watch: WatchView | null;
  saving?: boolean;
  onSave: (id: string, input: UpdateWatchInput) => void;
  onClose: () => void;
}

/** Frequency, extra search terms and languages for one watch */
export const WatchEditorDialog: React.FC<WatchEditorDialogProps> = ({ watch, saving, onSave, onClose }) => {
  const [frequency, setFrequency] = useState<WatchFrequency>('weekly');
  const [keywords, setKeywords] = useState('');
  const [languages, setLanguages] = useState('');

  useEffect(() => {
    if (!watch) return;
    setFrequency(watch.frequency);
    setKeywords(watch.keywords.join(', '));
    setLanguages(watch.languages.join(', '));
  }, [watch]);

  const invalidLanguages = toList(languages).some((code) => !/^[a-z]{2}$/i.test(code));

  return (
    <Dialog open={!!watch} onClose={onClose} labelledBy="watch-editor-title" handle className="sm:max-w-md">
      <DialogHeader
        id="watch-editor-title"
        eyebrow={watch?.subject.label}
        title="Edit watch"
        onClose={onClose}
      />
      <DialogBody className="space-y-4">
        <Field label="Frequency" htmlFor="watch-frequency" hint="Paused keeps the history but stops scanning.">
          <Select
            id="watch-frequency"
            value={frequency}
            onChange={(event) => setFrequency(event.target.value as WatchFrequency)}
          >
            {FREQUENCIES.map((value) => (
              <option key={value} value={value}>
                {FREQUENCY_LABEL[value]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Keywords" htmlFor="watch-keywords" hint="Aliases and spellings, comma separated.">
          <Input
            id="watch-keywords"
            value={keywords}
            onChange={(event) => setKeywords(event.target.value)}
            placeholder="e.g. Diallo, energy transition"
          />
        </Field>

        <Field
          label="Languages"
          htmlFor="watch-languages"
          hint="Two-letter codes, comma separated."
          error={invalidLanguages ? 'Use two-letter codes, e.g. fr, en' : undefined}
        >
          <Input
            id="watch-languages"
            value={languages}
            onChange={(event) => setLanguages(event.target.value)}
            aria-invalid={invalidLanguages}
            placeholder="fr, en"
          />
        </Field>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={saving}
          disabled={invalidLanguages}
          onClick={() =>
            watch &&
            onSave(watch.id, {
              frequency,
              keywords: toList(keywords),
              languages: toList(languages).map((code) => code.toLowerCase()),
            })
          }
        >
          Save
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
